<template>
  <div>
    <right-popup
      :show="isOpen || showFilter"
      :border-left="false"
      :border-top="true"
      :width="300"
      :exclude-header="true"
      :exclude-header-toolbar="excludeHeaderToolbar"
      :exclude-footer="excludeFooter"
      :custom-right-position-history="isHistory"
      :custom-right-position-export="isExport"
      :custom-styles="additionalStyles"
      :portal-name="isExport || isHistory ? 'portal-filter-second_popup' : 'portal-filter-sidebar'"
    >
      <template #body>
        <div v-if="currentFilter">
          <component
            :is="componentName"
            :key="componentName"
            v-click-outside="onClickOutside"
            :filter="filterConfig"
            :saved-filters="preparedSavedFilters"
            :component-name="componentName"
            :is-show-notification-message="isShowNotificationMessage"
            @changeFilter="changeFilter"
            @setFilter="applySavedFilter"
            @onSaveClick="onSaveClick"
            @onSave="onSave"
            @onResetClick="resetFilter"
            @onClose="close"
            @showHelpPopup="showHelpPopup"
            @backClick="changeComponent('filterMain');"
          />
        </div>
      </template>
    </right-popup>
  </div>
</template>

<script>
import app from '$$app';
import rightPopup from '../common/VueComponents/popups/rightPopup/rightPopup.vue';
import filterMain from './filterViews/filterMain.vue';
import filterSaving from './filterViews/filterSaving.vue';
import filterHelpers from './helpers';
import helpPopup from '../help/helpPopup';
import Filter from './filter';
import filterConstants from './constants';
import projectsModel from '$$projectModel';
import globalToast from '$$vueCmp/toast';

const FILTER_MODE = {
  main: 'mainFilter',
  saving: 'savingFilter',
  editing: 'editingFilter',
};

const OUTSIDE_SELECTORS = [
  '.help_block',
  '.toolbar_filter_button',
  '.export_filter_button',
  '.right-bar',
];

export default {
  name: 'AppFilter',
  components: { rightPopup, filterMain, filterSaving },
  props: {
    showFilter: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      componentName: 'filterMain',
      filterConfig: {
        id: null,
        name: null,
        type: null,
        properties: null,
        staticFilterItems: [],
        customColumnsItems: [],
        mode: FILTER_MODE.main,
      },
      isShowNotificationMessage: false,
      resetFilterAfterFirstInitIfNeeded: false,
    };
  },
  computed: {
    excludeFooter() {
      return !([filterConstants.FILTERS.sharedGantt, filterConstants.FILTERS.history].includes(this.filterConfig.type)) && !this.$store.getters.isExport;
    },

    excludeHeaderToolbar() {
      return this.filterConfig.type !== filterConstants.FILTERS.sharedGantt;
    },

    isOpen() {
      return this.$store.getters['filter/isOpen'];
    },
    isActive() {
      return this.$store.getters['filter/isActive'];
    },
    isNotFound() {
      return this.$store.getters['filter/isNotFound'];
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    savedFilters() {
      return this.$store.getters['filter/getAllSavedFilters'];
    },
    preparedSavedFilters() {
      let filters = [];

      if (this.currentFilter) {
        filters = filterHelpers.getSavedFilters(this.savedFilters, this.currentFilter);
      }

      if (filters.length) {
        filters = filters.map(item => ({
          ...item,
          iconButtons: [
            {
              icon: { name: 'pensil', size: 20 },
              size: 'small',
              handler: this.editFilterClick,
            },
            {
              icon: { name: 'delete', size: 20 },
              size: 'small',
              handler: this.removeFilterClick,
            },
          ],
        }));
      }

      return filters;
    },
    // customColumns() {
    //   return this.$store.getters['columns/getAllCustomColumns'];
    // },
    // allResources() {
    //   return this.$store.getters['resourcesModel/getAllResources'];
    // },
    isHistory() {
      return this.currentFilter?.type === filterConstants.FILTERS.history;
    },
    isExport() {
      return this.$store.getters.isExport;
    },
    additionalStyles() {
      const { currentFilter } = this;

      if (!currentFilter) return {};

      const { isBase, isExport, isLink } = this.$store.getters;
      const { FILTERS } = filterConstants;

      const currentRouteName = this.$route?.name || '';
      const specialRouteNames = ['progressReports', 'timeReports', 'budgetReports', 'reportsDetail', 'workload', 'userLogs'];
      // const sharedFilter = [FILTERS.sharedBoard, FILTERS.sharedGantt].includes(currentFilter.type);
      // const isTop90 = (isBase && specialRouteNames.includes(currentRouteName)) || sharedFilter;

      if ((isBase && specialRouteNames.includes(currentRouteName)) || isLink) return { top: '89px' };
      if (currentFilter.type === FILTERS.history || isExport) return { top: '49px' };

      return { top: '115px' };
    },
    advancedSettings() {
      return this.$store.getters['filter/getAdvancedSettingsByFilterType'](this.currentFilter?.type);
    },
    isLink() {
      return this.$store.getters.isLink;
    },
    ignoreOutsideSelectors() {
      const dateRangePickerSelectors = ['.vc-week', '.js-vgp-date-range', '.vc-nav-item'];

      return [...OUTSIDE_SELECTORS, ...dateRangePickerSelectors];
    },
  },
  watch: {
    currentFilter() {
      this.init();
    },
    isNotFound(flag) {
      if (flag && this.currentFilter) {
        filterHelpers.prepareFilterToastNotificationForExport(true);
        globalToast.info(__('filter_no_items_to_display'));
        filterHelpers.prepareFilterToastNotificationForExport(false);

        if (this.resetFilterAfterFirstInitIfNeeded) {
          this.$store.dispatch('filter/resetFilter');
        }
      }
    },
    advancedSettings: {
      handler(settings) {
        if (settings) {
          this.updateFilterUIByAdvancedSettings(settings);
        }
      },
      // immediate: true,
    },
  },
  beforeCreate() {
    const component = this;

    this.appListeners = [];
    this.projectsListeners = [];

    this.appListeners.push(app.on('onAfterCollaboration', event => {
      if (component.currentFilter) {
        const projectEvents = ['ProjectUpdated', 'ProjectArchived', 'ProjectRestored', 'ProjectDeleted'];

        if (projectEvents.includes(event.event)) {
          Filter.updateProjectIdsByRoute(this.$route);
          const updatedProps = filterHelpers.checkFilterValues(component.currentFilter.properties, Filter.projectIds);

          if (updatedProps) {
            component.updateCurrentFilter({ properties: updatedProps });
          } else {
            component.updateFilterItems();
          }
        }
      }
    }));

    this.appListeners.push(app.on('project:archive', (projectId, isArchived) => {
      if (this.currentFilter) {
        Filter.updateProjectIdsByRoute(this.$route);

        if (isArchived) {
          this.sendUpdatesIfNeeded();

          return;
        }

        if (this.currentFilter.id) {
          const savedFilter = this.preparedSavedFilters.find(f => f.id === this.currentFilter.id);

          this.applySavedFilter({
            id: savedFilter.id,
            name: savedFilter.name,
            type: savedFilter.type,
            properties: savedFilter.filter,
          });

          return;
        }

        this.init();
      }
    }));

    this.appListeners.push(app.on('closeWindowOnEscKeyPressed', () => {
      if (component.isOpen) {
        component.close();
      }
    }));

    this.projectsListeners.push(projectsModel.data.attachEvent('projectListRefreshed', () => {
      if (this.currentFilter) {
        Filter.updateProjectIdsByRoute(this.$route);

        if (this.currentFilter.id) {
          const savedFilter = this.preparedSavedFilters.find(f => f.id === this.currentFilter.id);

          this.applySavedFilter({
            id: savedFilter.id,
            name: savedFilter.name,
            type: savedFilter.type,
            properties: savedFilter.filter,
          });

          return;
        }

        const checkedValues = filterHelpers.checkFilterValues(this.currentFilter.properties, Filter.projectIds);

        if (checkedValues) {
          const updatedFilter = {
            ...this.currentFilter,
            id: null,
            name: null,
            properties: checkedValues,
          };

          this.updateCurrentFilter(updatedFilter);
        } else {
          this.updateCurrentFilter(this.currentFilter); //
        }
      }
    }));

    component.$store.subscribe((mutation, state) => {
      if (component.currentFilter) {
        if (mutation.type === 'resourcesModel/updateResource') {
          component.updateFilterItems();
        }
        if (mutation.type === 'resourcesModel/addResources') {
          component.updateFilterItems();
        }
        if (mutation.type === 'resourcesModel/removeResources') {
          const updatedProps = filterHelpers.checkFilterValues(component.currentFilter.properties, Filter.projectIds);

          if (updatedProps) {
            component.updateCurrentFilter({ properties: updatedProps });
          } else {
            component.updateFilterItems();
          }
        }
        if (mutation.type === 'columns/addColumnsToProjects') {
          if (Filter.projectIds.some(id => mutation.payload.projectIds.includes(id))) {
            component.initCustomColumnsFilter();
            component.updateFilterItems();
          }
        }
        if (mutation.type === 'columns/removeColumnFromProjects') {
          if (Filter.projectIds.some(id => mutation.payload.projectIds.includes(id))) {
            component.removeColumnById(mutation.payload.columnId);
          }
        }
        if (mutation.type === 'columns/removeCustomColumn') {
          if (Filter.customColumnsFilterItems.some(item => item.columnId === mutation.payload)) {
            component.removeColumnById(mutation.payload);
          }
        }
        if (mutation.type === 'columns/updateCustomColumn') {
          component.initCustomColumnsFilter();
          component.updateFilterItems();
        }
        if (mutation.type === 'columns/updateProject') {
          if (Filter.projectIds.some(id => id === mutation.payload.projectId) && mutation.payload.updated.columns) {
            const newColumnsIds = mutation.payload.updated.columns.filter(id => !Filter.customColumnsFilterItems.some(item => item.columnId === id));
            const deletedColumns = Filter.customColumnsFilterItems.filter(item => !mutation.payload.updated.columns.includes(item.columnId));

            deletedColumns.forEach(col => {
              const filterColumn = Filter.customColumnsFilterItems.find(item => item.columnId === col.columnId);
              const index = Filter.customColumnsFilterItems.indexOf(filterColumn);

              Filter.customColumnsFilterItems.splice(index, 1);
            });

            newColumnsIds.forEach(id => {
              Filter.customColumnsFilterItems.push(filterHelpers.createCustomColumnItem(id));
            });

            component.updateFilterItems();
          }
        }

        if (mutation.type === 'teamSetting/updateProjectsWithStatus') {
          component.updateFilterItems();
        }
      }
    });
  },
  created() {
    this.init();
    this.initRouterListener();
  },
  beforeUnmount() {
    this.appListeners?.forEach(id => app.off(id));
    this.projectsListeners?.forEach(id => projectsModel.data.detachEvent(`${id}`));

    this.appListeners = [];
    this.projectsListeners = [];
  },
  methods: {
    init() {
      this.resetFilterAfterFirstInitIfNeeded = false;
      if (this.currentFilter) {
        this.initFilter();
        this.checkActiveFilterIfValuesEnabled();
      } else {
        this.resetState();
        this.$store.commit('filter/setIsActiveFilter', false);
        this.$store.commit('filter/isNotFound', false);
      }

      this.$store.getters.isBase && this.$store.commit('headerView/setUIConfig');
    },
    initRouterListener() {
      this.$router?.beforeEach((to, from, next) => {
        try {
          const ignoreList = ['progressReports', 'budgetReports', 'timeReports'];
          const needClose = !ignoreList.includes(to.name);

          this.isOpen && needClose && this.close();
        } finally {
          next();
        }
      });
    },
    initFilter() {
      this.initCustomColumnsFilter();
      if (this.currentFilter.type !== this.filterConfig?.type) {
        this.resetFilterAfterFirstInitIfNeeded = true;
        this.setFilterItems();
      } else {
        this.updateFilterItems();
      }
    },
    initCustomColumnsFilter() {
      Filter.customColumnsFilterItems = [];
      const projectModeWithoutCustomColumns = this.isHistory;

      if (!projectModeWithoutCustomColumns && filterHelpers.checkCustomColumnsAccessByRouteAndRights(this.$route)) {
        Filter.initCustomColumnsFilter(this.currentFilter.properties);
      }
    },
    setFilterItems() {
      const staticFilterItems = filterHelpers.prepareStaticItems(this.currentFilter.type, Filter.staticFilterItems, this.advancedSettings);

      this.filterConfig = {
        ...this.filterConfig,
        ...this.currentFilter,
        staticFilterItems,
        customColumnsItems: Filter.customColumnsFilterItems,
      };
    },
    updateFilterItems() {
      Filter.updateFilterItemsWithCurrentValues(this.currentFilter);
      Filter.updateCustomColumnsItemsWithCurrentValues(this.currentFilter.properties);

      this.setFilterItems();
    },
    updateCurrentFilter(data) {
      this.$store.commit('filter/updateCurrentFilter', data);
    },
    checkActiveFilterIfValuesEnabled() {
      const isActiveFilter = filterHelpers.checkEnabledValues(this.currentFilter.properties);

      this.$store.commit('filter/setIsActiveFilter', isActiveFilter);
    },
    changeFilter(data) {
      this.$store.commit('filter/isNotFound', false);

      if ('customColumns' in data && 'customColumns' in this.currentFilter.properties) {
        const customColumnsFilterOptions = {
          ...this.currentFilter.properties.customColumns,
          ...data.customColumns,
        };

        data.customColumns = customColumnsFilterOptions;
      }

      const updatedValues = {
        ...this.currentFilter.properties,
        ...data,
      };

      this.updateCurrentFilter({ id: null, name: null, properties: updatedValues });
      this.sendAnalyticMessage();
    },
    updateActiveFilter() {
      const { isExport } = this.$store.getters;

      if (this.currentFilter && !this.isHistory && !isExport) {
        this.$store.dispatch('filter/updateActiveFilter');
      }
    },
    changeComponent(componentName) {
      if (componentName === 'filterMain') {
        this.updateFilterItems();
      }

      this.componentName = componentName;
    },
    updateFilterName(data) {
      this.$store.dispatch('filter/updateFilterName', {
        id: data.id,
        name: data.name,
        type: this.filterConfig.type,
      });
    },
    onSaveClick() {
      const flatItems = filterHelpers.prepareFlatFilterItems(Filter.staticFilterItems, this.advancedSettings);

      this.filterConfig = {
        ...this.filterConfig,
        id: null,
        name: null,
        staticFilterItems: flatItems,
        mode: FILTER_MODE.saving,
      };

      this.changeComponent('filterSaving');
    },
    onSave(data) {
      if (this.filterConfig.mode === FILTER_MODE.editing) {
        this.updateFilterName({ id: data.id, name: data.name });

        Filter.updateFilterItemsWithCurrentValues(this.currentFilter);
        Filter.updateCustomColumnsItemsWithCurrentValues(this.currentFilter.properties);

        this.filterConfig = {
          ...this.filterConfig,
          mode: FILTER_MODE.main,
          staticFilterItems: Filter.staticFilterItems,
          customColumnsItems: Filter.customColumnsFilterItems,
        };
      }

      if (this.filterConfig.mode === FILTER_MODE.saving) {
        if (this.preparedSavedFilters.length) {
          const isDuplicateName = this.preparedSavedFilters.find(filters => filters.name.toLowerCase() === data.name.trim().toLowerCase());

          if (isDuplicateName) {
            globalToast.warning(__('filter_save_error_duplicate_name'));

            return;
          }
        }

        this.filterConfig = {
          ...this.filterConfig,
          name: data.name,
        };

        userExtAnalytics.log('filter_saved');
        this.$store.dispatch('filter/saveFilter', data.name);
      }

      this.changeComponent('filterMain');
    },
    editFilterClick(filter) {
      Filter.updateFilterItemsWithCurrentValues(filter);
      Filter.updateCustomColumnsItemsWithCurrentValues(filter.filter);

      this.filterConfig = {
        ...this.filterConfig,
        id: filter.id,
        name: filter.name,
        properties: filter.filter,
        staticFilterItems: Filter.staticFilterItems,
        customColumnsItems: Filter.customColumnsFilterItems,
        mode: FILTER_MODE.editing,
      };
      this.changeComponent('filterSaving');
    },
    removeFilterClick(filter) {
      this.$store.dispatch('filter/removeFilter', { type: filter.type, filterId: filter.id });
    },
    resetFilter() {
      if (this.isExport) {
        const emptyProperties = filterHelpers.getEmptyFilter();

        this.updateCurrentFilter({ id: null, name: null, properties: emptyProperties });
      } else {
        this.$store.dispatch('filter/resetFilter');
        this.sendAnalyticMessage();
      }
    },
    onClickOutside(e) {
      const currentRouteMode = this.$route?.params.mode || '';

      if (this.isOpen && this.ignoreOutsideSelectors.some(selector => e.target.closest(`${selector}`))) {
        return;
      }

      if (this.isOpen && currentRouteMode === 'export') return;

      if (this.isOpen) {
        this.close();
      }
    },
    close() {
      if (this.isNotFound) {
        this.resetFilter();
      }
      this.updateActiveFilter();
      this.$store.commit('filter/toggleFilter', false);
      this.changeComponent('filterMain');
    },
    resetState() {
      this.filterConfig = {
        id: null,
        name: null,
        type: null,
        properties: null,
        staticFilterItems: [],
        customColumnsItems: [],
        mode: FILTER_MODE.main,
      };
    },
    showHelpPopup({ event, id }) {
      helpPopup.showHelpBlock(event, id);
    },
    sendAnalyticMessage() {
      if (!this.currentFilter) return;

      const analyticParams = filterHelpers.getAnalitycsParams(this.$route, this.currentFilter.properties, this.filterConfig.type, !!this.filterConfig.id);

      if (analyticParams) {
        userExtAnalytics.log(analyticParams.event, analyticParams.params);
      }
    },
    removeColumnById(columnId) {
      const filterCustomColumns = this.currentFilter.properties.customColumns;

      if (filterCustomColumns && columnId in filterCustomColumns) {
        delete filterCustomColumns[columnId];

        const updatedProps = {
          ...this.filterConfig.properties,
          customColumns: { ...filterCustomColumns },
        };

        this.updateCurrentFilter({ properties: updatedProps });
      } else {
        this.initCustomColumnsFilter();
        this.updateFilterItems();
      }
    },
    sendUpdatesIfNeeded() {
      const checkedValues = filterHelpers.checkFilterValues(this.currentFilter.properties, Filter.projectIds);

      if (checkedValues) {
        const updatedFilter = {
          ...this.currentFilter,
          id: null,
          name: null,
          properties: checkedValues,
        };

        this.updateCurrentFilter(updatedFilter);

        return true;
      }

      return false;
    },
    applySavedFilter(savedFilterData) {
      this.$store.commit('filter/isNotFound', false);

      const filterData = { ...savedFilterData };
      const checkedValues = filterHelpers.checkFilterValues(savedFilterData.properties, Filter.projectIds);

      if (checkedValues) {
        filterData.properties = checkedValues;
      }

      this.updateCurrentFilter(filterData);
      this.sendAnalyticMessage();
    },
    updateFilterUIByAdvancedSettings(settings) {
      const reportFilters = [
        filterConstants.FILTERS.reportsBudgetProject,
        filterConstants.FILTERS.reportsTimeProject,
        filterConstants.FILTERS.reportsBudgetResource,
        filterConstants.FILTERS.reportsTimeResource,
        filterConstants.FILTERS.reportsProgressSchedule,
      ];

      this.isShowNotificationMessage = settings?.isShowNotificationMessage || false;
      if (this.currentFilter && reportFilters.includes(this.currentFilter.type)) {
        this.setFilterItems();
      }
    },
  },
};
</script>

<style lang='less'></style>
